import logger from '../../utils/logger';

export interface PrecisionLossData {
  [pumpId: string]: number;
}

export interface PumpData {
  targetPrecisionLossAdjustment: number;
  precisionLossCalculated?: boolean;
}

// Store precision loss data by batch UID
const batchPrecisionLoss = new Map<string, PrecisionLossData>();

/**
 * Handles precision loss calculation and adjustment for a pump in a batch.
 * Carries forward precision loss due to target rounding to meter rate.
 * @returns The adjustment value to apply to the target
 */
export function handlePrecisionLoss(
  batchUid: string,
  pumpId: number,
  precisionLoss: number,
  pumpData: PumpData,
): number {
  // Initialize targetPrecisionLossAdjustment if not set
  if (pumpData.targetPrecisionLossAdjustment === undefined) {
    pumpData.targetPrecisionLossAdjustment = 0;
  }

  // Get or initialize precision loss for this batch
  if (!batchPrecisionLoss.has(batchUid)) {
    batchPrecisionLoss.set(batchUid, {});
  }
  const batchPrecisionData = batchPrecisionLoss.get(batchUid)!;

  if (!batchPrecisionData[pumpId]) {
    batchPrecisionData[pumpId] = 0;
  }

  // Only accumulate precision loss if not already calculated for this pump in this drop
  // This prevents multi-accumulation during pause/resume cycles
  if (!pumpData.precisionLossCalculated) {
    pumpData.precisionLossCalculated = true;

    // Accumulate precision loss for this pump
    batchPrecisionData[pumpId] += precisionLoss;

    // If accumulated loss > 0.5, we're under-delivering too much, so add 1
    if (batchPrecisionData[pumpId] > 0.5) {
      logger.info('precision loss adjustment: +1 for: ', pumpId);
      pumpData.targetPrecisionLossAdjustment += 1;
      batchPrecisionData[pumpId] -= 1;
    }
    // If accumulated loss < -0.5, we're over-delivering too much, so subtract 1
    else if (batchPrecisionData[pumpId] < -0.5) {
      logger.info('precision loss adjustment: -1 for: ', pumpId);
      pumpData.targetPrecisionLossAdjustment -= 1;
      batchPrecisionData[pumpId] += 1;
    }
  }

  return pumpData.targetPrecisionLossAdjustment;
}

/**
 * Cleans up precision loss data for a completed batch
 */
export function cleanupBatchPrecisionLoss(batchUid: string): void {
  batchPrecisionLoss.delete(batchUid);
}

/**
 * Calculates the adjusted target for display including precision loss adjustment
 */
export function calculateAdjustedTargetForDisplay(
  target: number,
  adjustment: number,
  cutOff: number,
  meterRate: number,
): number {
  const adjustedTarget = target + adjustment;
  // Target displayed on device MUST NOT include the cutoff, thus, we add it back
  return (adjustedTarget + Math.floor(cutOff / meterRate)) * meterRate;
}
